import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, ArrowRight, X } from 'lucide-react';
import { motion } from 'framer-motion';
import { useWishlist } from '../contexts/WishlistContext';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Recently Viewed Products - Shows at bottom of product pages
 */
const RecentlyViewed = ({ currentProductId, maxItems = 6 }) => {
  const { recentlyViewed, clearRecentlyViewed } = useWishlist();
  
  // Filter out current product and limit items
  const filteredProducts = recentlyViewed
    .filter(p => p.product_type !== currentProductId && p.id !== currentProductId)
    .slice(0, maxItems);

  if (filteredProducts.length === 0) return null;

  return (
    <section className="py-12 bg-neutral-50">
      <div className="container-xl">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-neutral-500" />
            <h3 className="text-lg font-semibold text-neutral-900">Recently Viewed</h3>
          </div>
          <button 
            onClick={clearRecentlyViewed}
            className="text-sm text-neutral-500 hover:text-neutral-700 transition-colors"
          >
            Clear History
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {filteredProducts.map((product, index) => {
            const imageUrl = product.image || '';
            const fullImageUrl = imageUrl.startsWith('/') ? `${API_URL}${imageUrl}` : imageUrl;
            const href = `/products/${product.product_type}`;

            return (
              <motion.div
                key={product.id || product.product_type}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Link 
                  to={href}
                  className="group block bg-white rounded-lg overflow-hidden border border-neutral-200 hover:border-amber-300 hover:shadow-md transition-all"
                >
                  <div className="aspect-square bg-neutral-100 relative overflow-hidden">
                    {fullImageUrl ? (
                      <img 
                        src={fullImageUrl}
                        alt={product.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=200';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-neutral-300">
                        No image
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <h4 className="text-sm font-medium text-neutral-900 truncate group-hover:text-amber-600 transition-colors">
                      {product.title}
                    </h4>
                    {product.price_from > 0 && (
                      <p className="text-xs text-neutral-500 mt-1">
                        From ${product.price_from}
                      </p>
                    )}
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default RecentlyViewed;
